// src/contracts/ops/OpsDashboardModel.ts
//
// Broadcast Ops Dashboard Data Model (v1)
// --------------------------------------
// Goals:
// - Witness-safe (no raw payloads, no PII)
// - Ops-friendly (bands, statuses, bounded lists)
// - Deterministic defaults (stable initial state)
// - Monetizable (enterprise-grade operate/control/prove surface)
//
// IMPORTANT:
// - Keep this file types-only + deterministic helper constructors.
// - Do NOT import runtime logic from app modules.
// - Avoid widening types; stability matters for dashboards and embeds.
//

/* ---------------------------------------
 * Core primitives
 * -------------------------------------- */

export type OpsHealth = "ok" | "degraded" | "faulted" | "blocked" | "unknown";

export type OpsSeverity = "info" | "warning" | "critical";

export type OpsBand = "normal" | "warning" | "critical" | "unknown";

/** Opaque capability identifier (e.g., "omnicast.surfaceWitness") */
export type CapabilityId = string;

/** Opaque operator/actor identifier (hashed id or stable token) */
export type OpsActorId = string;

/* ---------------------------------------
 * Root state
 * -------------------------------------- */

export interface OpsDashboardStateV1 {
  v: 1;

  /** When this state was last updated (ms). */
  updatedAt: number;

  /** Runtime identity / build lock (trust anchor). */
  identity: OpsRuntimeIdentity;

  /** Capability inventory + health status. */
  capabilities: OpsCapabilityHealthMap;

  /** High-level flow + throughput (aggregated counters only). */
  flow: OpsEventFlow;

  /** Latency + degradation bands. */
  latency: OpsLatencyHealth;

  /** Contract and boundary violations (summaries only). */
  violations: OpsViolationState;

  /** Actions available + last actions taken. */
  control: OpsControlState;

  /** Execution timeline (evidence). */
  timeline: OpsExecutionTimeline;

  /** Operator action ledger (accountability). */
  ledger: OpsOperatorLedger;
}

/* ---------------------------------------
 * Identity (Operate)
 * -------------------------------------- */

export interface OpsRuntimeIdentity {
  /** Stable build identifier (e.g., git hash or release tag). */
  buildId: string;

  /** Checksum of installed manifest set (string hash). */
  manifestChecksum: string;

  /** Environment tag. */
  env: "prod" | "staging" | "dev" | "unknown";

  /** When runtime declared itself ready (ms). */
  goLiveAt: number | null;

  /** Current entitlement tier at runtime. */
  tier: "public" | "creator" | "enterprise" | "regulated" | "unknown";

  /** Authority source fingerprint (redacted). */
  authoritySource: {
    kind: "user" | "service" | "unknown";
    id: string; // opaque / hashed
  };

  /** Runtime mode determined from health + policy. */
  mode: "live" | "degraded" | "safe" | "unknown";

  /** Optional wiring manifest summary (safe) */
  manifestSummary?: {
    v: 1;
    nodes: number;
    routes: number;
    providers: number;
    bridges: number;
    checks: number;
  };

  /** Optional human label (event name). */
  label?: string;
}

/* ---------------------------------------
 * Capability inventory + health (Operate)
 * -------------------------------------- */

export interface OpsCapabilityHealth {
  id: CapabilityId;

  tierRequired: "public" | "creator" | "enterprise" | "regulated";
  safety: "witness" | "authority" | "transformer";

  status: OpsHealth;

  /** Optional short reason code (no stack traces). */
  reason?: string;

  /** Last time it emitted a heartbeat/metric (ms). */
  lastSeenAt: number | null;

  /** Dependency ids it expects (for warnings). */
  requires?: CapabilityId[];

  /** Whether ops disabled it (control plane). */
  disabledByOps?: boolean;
}

export type OpsCapabilityHealthMap = Record<CapabilityId, OpsCapabilityHealth>;

/* ---------------------------------------
 * Flow counters (Operate)
 * -------------------------------------- */

export interface OpsFlowWindow {
  /** Window length in seconds (e.g. 10, 60, 300). */
  windowSec: number;

  /** Count of events observed in this window. */
  count: number;

  /** Dropped/throttled count (if known). */
  dropped?: number;
}

export interface OpsEventFlow {
  /** Aggregated counters by channel/event name (no payload). */
  channels: Record<
    string,
    {
      lastAt: number | null;
      windows: OpsFlowWindow[];
    }
  >;

  /** Global flow status band derived from channels. */
  band: OpsBand;
}

/* ---------------------------------------
 * Latency & degradation (Operate)
 * -------------------------------------- */

export interface OpsLatencyMetric {
  /** What we are measuring (safe). */
  name: string;

  /** Band classification for ops. */
  band: OpsBand;

  /** Optional numeric hint (safe, non-sensitive). */
  p95Ms?: number;

  lastAt: number | null;
}

export interface OpsLatencyHealth {
  band: OpsBand;

  /** Known latency metrics (small fixed set). */
  metrics: OpsLatencyMetric[];

  /** Degradation flags (ops-friendly). */
  degradedReasons: string[];
}

/* ---------------------------------------
 * Violations (Safety)
 * -------------------------------------- */

export interface OpsViolation {
  severity: OpsSeverity;

  /** The channel involved (no payload). */
  channel: string;

  /** Where it was detected (capability id or module name). */
  where: string;

  /** Short code like "schema_invalid" | "boundary_breach". */
  reason: string;

  /** Optional schema name, if relevant. */
  schema?: string;

  at: number;
}

export interface OpsViolationState {
  /** Rolling list of recent violations (bounded by reducer). */
  recent: OpsViolation[];

  /** Aggregated counts by reason. */
  countsByReason: Record<string, number>;

  /** Overall band derived from counts/severity. */
  band: OpsBand;
}

/* ---------------------------------------
 * Control (Control)
 * -------------------------------------- */

export interface OpsControlActionAvailability {
  canDisableCapabilities: boolean;
  canForceFallback: boolean;

  /** Optional restrictions (short codes). */
  reasonIfDisabled?: string;
}

export interface OpsControlState {
  availability: OpsControlActionAvailability;

  fallback: {
    active: boolean;
    at: number | null;
    reason?: string;
  };

  disabledCapabilities: Record<
    CapabilityId,
    { at: number; actor: OpsActorId; reason?: string }
  >;
}

/* ---------------------------------------
 * Evidence timeline (Prove)
 * -------------------------------------- */

export type OpsTimelineEventKind =
  | "go_live"
  | "capability_installed"
  | "capability_uninstalled"
  | "capability_blocked"
  | "capability_faulted"
  | "policy_decision"
  | "ops_action"
  | "export_proof_bundle";

export interface OpsTimelineEvent {
  kind: OpsTimelineEventKind;
  at: number;

  /** Strictly small, redacted details. */
  detail: Record<string, string | number | boolean | null | undefined>;
}

export interface OpsExecutionTimeline {
  /** Rolling list of timeline events (bounded by reducer). */
  events: OpsTimelineEvent[];

  /** Proof bundle export status. */
  proofBundle: {
    available: boolean;
    lastExportAt: number | null;
    lastExportId?: string;
  };
}

/* ---------------------------------------
 * Operator ledger (accountability)
 * -------------------------------------- */

export interface OpsOperatorAction {
  at: number;
  actor: OpsActorId;
  action:
    | "disable_capability"
    | "enable_capability"
    | "force_fallback"
    | "clear_fallback";
  target?: CapabilityId;
  reason?: string;
}

export interface OpsOperatorLedger {
  /** Rolling actions list (bounded by reducer). */
  actions: OpsOperatorAction[];

  /** Counts by action type. */
  counts: Record<string, number>;
}

/* ---------------------------------------
 * Deterministic initial state
 * -------------------------------------- */

export function createEmptyOpsDashboardState(): OpsDashboardStateV1 {
  return {
    v: 1,
    updatedAt: Date.now(),
    identity: {
      buildId: "unknown",
      manifestChecksum: "unknown",
      env: "unknown",
      goLiveAt: null,
      tier: "unknown",
      authoritySource: { kind: "unknown", id: "unknown" },
      mode: "unknown",
    },
    capabilities: {},
    flow: { channels: {}, band: "unknown" },
    latency: { band: "unknown", metrics: [], degradedReasons: [] },
    violations: { recent: [], countsByReason: {}, band: "unknown" },
    control: {
      availability: { canDisableCapabilities: false, canForceFallback: false },
      fallback: { active: false, at: null },
      disabledCapabilities: {},
    },
    timeline: { events: [], proofBundle: { available: false, lastExportAt: null } },
    ledger: { actions: [], counts: {} },
  };
}

